import React, { useEffect, useState } from 'react';
import { LIVEDETECT_BASE as BASE, bust } from './useLatestDetection';
import LiveDetectLightbox from './LiveDetectLightbox';
import '../css/liveDetect.css';

const fmtAgo = (epochS) => {
  const dt = Math.round(Date.now() / 1000 - epochS);
  if (dt < 60) return `vor ${dt} s`;
  if (dt < 3600) return `vor ${Math.round(dt / 60)} Min`;
  return `vor ${Math.round(dt / 3600)} h`;
};

const fmtEpoch = (epochS) => {
  if (!epochS) return '';
  try {
    return new Date(epochS * 1000).toLocaleString('de-DE', {
      timeZone: 'Europe/Berlin',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return '';
  }
};

const LiveDetect = ({ detection, index }) => {
  const { latest, error: latestError, isStale } = detection;
  const [openGroupId, setOpenGroupId] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [, setTick] = useState(0);

  // Re-render every second so "vor X s" stays current
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const nowEpoch = Date.now() / 1000;
  const indexStale = index && index.generated_at && (nowEpoch - index.generated_at) > 600;

  return (
    <div className="livedetect">
      {/* Live tile */}
      <div className="livedetect-live">
        {latestError && (
          <div className="livedetect-error">
            <i className="fa fa-exclamation-triangle"></i>
            Backend nicht erreichbar
          </div>
        )}
        {!latestError && !latest && (
          <div className="livedetect-empty">
            <i className="fa fa-eye"></i>
            <div className="livedetect-empty-title">Noch keine Erkennung</div>
            <div className="livedetect-empty-text">Sobald ein Zug erkannt wird, erscheint er hier.</div>
          </div>
        )}
        {!latestError && latest && (
          <>
            <div className="livedetect-image-wrap">
              <img
                key={latest.epoch}
                className={`livedetect-image ${isStale ? 'stale' : ''}`}
                src={bust(`${BASE}/${latest.image}`)}
                alt="Letzter erkannter Zug"
              />
              {!isStale && <span className="livedetect-live-badge">Live</span>}
            </div>
            <div className="livedetect-meta">
              <div className="livedetect-meta-row">
                <span className={`livedetect-ago ${isStale ? 'stale' : ''}`}>
                  <i className="fa fa-clock-o"></i> {fmtAgo(latest.epoch)}
                </span>
                <span className="livedetect-timestamp">{fmtEpoch(latest.epoch)}</span>
              </div>
              {latest.best_detection && (
                <div className="livedetect-meta-row">
                  <span className="livedetect-class">
                    <i className="fa fa-train"></i> {latest.best_detection.class_name}
                  </span>
                  {latest.best_detection.confidence != null && (
                    <span className="livedetect-confidence">
                      {Math.round(latest.best_detection.confidence * 100)}%
                    </span>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Gallery */}
      <div className="livedetect-gallery">
        <div className="livedetect-gallery-header">
          <h4>Letzte Erkennungen</h4>
          {index && (
            <div className="livedetect-gallery-stats">
              {index.group_count} Gruppen · {index.ring_used}/{index.ring_size} Frames
              {indexStale && (
                <span className="livedetect-stale-warn">
                  <i className="fa fa-exclamation-circle"></i> Stream möglicherweise gestört
                </span>
              )}
            </div>
          )}
        </div>

        {!index && <div className="livedetect-loading">Lade Galerie…</div>}
        {index && index.groups.length === 0 && (
          <div className="livedetect-empty-small">Noch keine erkannten Züge im Ringpuffer.</div>
        )}

        {index && index.groups.length > 0 && (
          <div className="livedetect-grid">
            {index.groups.map((group, idx) => {
              const isExpanded = openGroupId === group.id;
              const passNumber = index.groups.length - idx;
              return (
                <div key={group.id} className="livedetect-card">
                  <div className="livedetect-card-header">
                    <span className="livedetect-card-title">
                      <i className="fa fa-train"></i> Zug-Durchgang #{passNumber}
                    </span>
                    <span className="livedetect-card-time">{fmtEpoch(group.start_epoch)}</span>
                  </div>
                  <img
                    className="livedetect-card-image"
                    src={`${BASE}/ring/${group.best.image}`}
                    alt=""
                    onClick={() => {
                      const bestIdx = group.frames.findIndex(f => f.image === group.best.image);
                      setLightbox({ frames: group.frames, initialIndex: bestIdx >= 0 ? bestIdx : 0 });
                    }}
                    title="Anklicken um vergrößert anzuzeigen"
                  />
                  <div className="livedetect-card-meta">
                    <div className="livedetect-card-stats">
                      <span title="Anzahl Frames"><i className="fa fa-film"></i> {group.frame_count}</span>
                      <span title="Dauer"><i className="fa fa-hourglass-half"></i> {group.duration_s.toFixed(1)} s</span>
                      <span className="livedetect-card-confidence" title="Beste Konfidenz">
                        {Math.round(group.best.confidence * 100)}%
                      </span>
                    </div>
                  </div>
                  <button
                    className={`livedetect-expand-bar ${isExpanded ? 'open' : ''}`}
                    onClick={() => setOpenGroupId(isExpanded ? null : group.id)}
                  >
                    <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                    {isExpanded
                      ? 'Alle Bilder ausblenden'
                      : `Alle ${group.frame_count} Bilder anzeigen`}
                  </button>
                  {isExpanded && (
                    <div className="livedetect-card-frames">
                      {group.frames.map((frame, fIdx) => (
                        <img
                          key={frame.image}
                          src={`${BASE}/ring/${frame.image}`}
                          alt=""
                          onClick={() => setLightbox({ frames: group.frames, initialIndex: fIdx })}
                          title={`${fmtEpoch(frame.epoch)} — ${Math.round(frame.confidence * 100)}% — anklicken um vergrößert anzuzeigen`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <LiveDetectLightbox
        frames={lightbox?.frames}
        initialIndex={lightbox?.initialIndex || 0}
        onClose={() => setLightbox(null)}
      />
    </div>
  );
};

export default LiveDetect;
